import {
  type DataGridProProps,
  GridRow,
  type GridRowProps,
  type RowPropsOverrides,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { useEffect, useMemo } from "react";
import { ServerStream, type WorkPacket, workPacketTypeToServerStreamMap } from "src/types/work-packets";
import type { BaseCase } from "../Cases/case-types";
import type { WorkPacketType } from "../WorkPacketType";
import { useAccountScopeDeactivationDatesMap } from "../WorkPacketsGrid/filters/hooks/useAccountOptions";
import { ActivationStatusTooltip } from "./ActivationStatusTooltip";
import type { AccountScopeDeactivationDates, ActivationState } from "./activation-status-types";
import { evaluateActivationStatusForStream } from "./activation-status-utils";

import css from "./useCustomRowDisplay.module.css";
import clsx from "clsx";
import { Dayjs } from "dayjs";

const activationStateToCssClassMap: Record<ActivationState, string> = {
  active: "",
  sunset: css.sunsetStore,
  inactive: css.deactivatedStore,
};

export function useCustomWorkPacketRowDisplay() {
  const accountsScopeInfoMap = useAccountScopeDeactivationDatesMap();

  const slots = useMemo<DataGridProProps["slots"]>(
    () => ({ row: createCustomRowComponent(accountsScopeInfoMap, workPacketRowDataGetter) }),
    [accountsScopeInfoMap],
  );

  return { slots };
}

export function useCustomCaseRowDisplay(workPacketType: WorkPacketType) {
  const accountsScopeInfoMap = useAccountScopeDeactivationDatesMap();
  const caseRowDataGetter = useMemo(() => createCaseRowDataGetter(workPacketType), [workPacketType]);

  const slots = useMemo<DataGridProProps["slots"]>(
    () => ({ row: createCustomRowComponent(accountsScopeInfoMap, caseRowDataGetter) }),
    [accountsScopeInfoMap, caseRowDataGetter],
  );

  return { slots };
}

type RowDataGetter<T> = (row: T) => {
  storeId: number;
  recoveryStreamServer: ServerStream;
  expiredAt: Dayjs | undefined;
};

const workPacketRowDataGetter: RowDataGetter<WorkPacket> = row => ({
  storeId: row.storeId,
  recoveryStreamServer: row.recoveryStreamServer,
  expiredAt: row.expiredAt,
});

const createCaseRowDataGetter =
  (workPacketType: WorkPacketType): RowDataGetter<BaseCase> =>
  (row: BaseCase) => ({
    storeId: row.storeId,
    recoveryStreamServer: workPacketTypeToServerStreamMap[workPacketType],
    expiredAt: undefined,
  });

function createCustomRowComponent<T extends WorkPacket | BaseCase>(
  accountScopeDeactivationDatesMap: Map<number, AccountScopeDeactivationDates>,
  rowDataGetter: RowDataGetter<T>,
) {
  return function CustomGridRow(props: GridRowProps & RowPropsOverrides) {
    const apiRef = useGridApiContext();
    const row = props.row as T;
    const { storeId, recoveryStreamServer, expiredAt } = rowDataGetter(row);
    const accountScopeDeactivationDates = accountScopeDeactivationDatesMap.get(storeId);
    const evaluatedActivationStatus = evaluateActivationStatusForStream(
      accountScopeDeactivationDates,
      recoveryStreamServer,
    );
    const { state, deactivationDate } = evaluatedActivationStatus;

    useEffect(() => {
      // only consider work packet rows - cases use different logic
      if (state !== "active" && "packetId" in row && !row.evaluatedActivationStatus) {
        apiRef.current.updateRows([{ packetId: row.packetId, evaluatedActivationStatus }]);
      }
    }, [apiRef, evaluatedActivationStatus, row, state]);
    // if (state === "active") return <GridRow {...props} />;

    const className = activationStateToCssClassMap[state] || clsx(expiredAt && css.expiredPacket);
    return (
      <ActivationStatusTooltip
        activationState={state}
        deactivationDate={deactivationDate}
        recoveryStream={recoveryStreamServer}
        expiredAt={expiredAt}
      >
        <GridRow {...props} className={className} />
      </ActivationStatusTooltip>
    );
  };
}
